import { useEffect, useRef, useState } from 'react';
import { JoinRoom, emitUserOnline } from '../ClientSocket/ClientSocket';
import config from '../config.js';

const Login = ({ onLoginSuccess, onShowRegister }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const [inviteJoinError, setInviteJoinError] = useState('');
  const inviteJoinAttemptedRef = useRef(false);

  const urlParams = new URLSearchParams(window.location.search);
  const inviteToken = urlParams.get('guestInvite');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try 
    {
      const response=await fetch(`${config.API_URL}/api/v1/auth/login`,{
        method:'POST',
        headers:
        {
            'Content-Type':'application/json'
        },
        credentials: 'include',
        body:JSON.stringify({
            username:formData.username,
            password:formData.password
        })
      })
      const data=await response.json()
      if(!data.success)
      {
        setError(data.message || "Login failed");
      }
      else
      {
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        
        await fetch(`${config.API_URL}/api/v1/users/presence`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${data.data.accessToken}`
            },
            body: JSON.stringify({ online: true })
        });

        JoinRoom(data.data.user._id)
        emitUserOnline(data.data.user._id)
        onLoginSuccess(data.data.user)
      }
      
    } 
    catch (err) 
    {
      setError(err.message || 'Login failed. Please try again.');
    } 
    finally 
    {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${config.API_URL}/api/v1/auth/google`;
  };

  const completeGuestLogin = (data) => {
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);
    localStorage.setItem('guestInviteLink', data.data.inviteLink || '');
    JoinRoom(data.data.user._id);
    onLoginSuccess(data.data.user, data.data.room, data.data.inviteLink || null);
  }

  const handleGuestStart = async () => {
    setError('');
    setInviteJoinError('');
    setIsGuestLoading(true);
    try {
      const response = await fetch(`${config.API_URL}/api/v1/auth/guest/start`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to start guest session');
      }
      completeGuestLogin(data);
    } catch (err) {
      setError(err.message || 'Unable to start guest session');
    } finally {
      setIsGuestLoading(false);
    }
  }

  const handleGuestJoin = async () => {
    if (!inviteToken) {
      return;
    }
    setError('');
    setInviteJoinError('');
    setIsGuestLoading(true);
    try {
      const response = await fetch(`${config.API_URL}/api/v1/auth/guest/join`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ inviteToken })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to join guest room');
      }
      localStorage.setItem('accessToken', data.data.accessToken);
      localStorage.setItem('refreshToken', data.data.refreshToken);
      JoinRoom(data.data.user._id);
      onLoginSuccess(data.data.user, data.data.room, null);
    } catch (err) {
      const message = err.message || 'Unable to join guest room';
      setInviteJoinError(message);
      setError(message);
    } finally {
      setIsGuestLoading(false);
    }
  }

  useEffect(() => {
    if (!inviteToken || inviteJoinAttemptedRef.current) {
      return;
    }

    inviteJoinAttemptedRef.current = true;
    handleGuestJoin();
  }, []);

  return (
   <div className="flex justify-center items-center min-h-screen p-4" 
      style={{
        backgroundImage: 'url(/bg-auth.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h2>
          <p className="text-gray-600">Sign in to continue to Zap</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-md mb-6 text-sm">
            {error}
          </div>
        )}

        {inviteToken && !isGuestLoading && inviteJoinError && (
          <div className="bg-amber-50 text-amber-700 p-4 rounded-md mb-6 text-sm border border-amber-200">
            Invite join failed: {inviteJoinError}. You can still start your own temporary guest room below.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter your username"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>
          
          <button 
            type="submit" 
            className={`w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition duration-200 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="mt-6 w-full flex justify-center items-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition duration-200"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <button
            onClick={handleGuestStart}
            disabled={isGuestLoading || isLoading}
            className={`mt-3 w-full flex justify-center items-center px-4 py-3 rounded-md text-sm font-medium transition duration-200 ${
              isGuestLoading || isLoading
                ? 'bg-amber-100 text-amber-700 cursor-not-allowed'
                : 'bg-amber-500 text-black hover:bg-amber-600'
            }`}
          >
            {isGuestLoading
              ? 'Preparing Demo Room...'
              : inviteToken
                ? 'Start Your Own Guest Room'
                : 'Try as Guest'}
          </button>
        </div>
        
        <div className="text-center mt-6 text-gray-600">
          <p>
            Don't have an account?
            <button
              type="button"
              onClick={onShowRegister}
              className="ml-1 text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;